import type { StateCreator } from 'zustand';
import type { StandardLoanState } from '../standardLoanState';
import type { BorrowerSelection } from '../../../../../common/components/v2/BorrowerSelector/constants/borrowerSelection';
import {
    BorrowerForm,
    BorrowerLoanContext,
    defaultBorrowerLoanContext,
    emptyBorrowerForm,
} from '../../components/Steps/components/SetupAndBorrower/Borrower/constants/borrowerForm';

/**
 * The Borrower section of the Setup & Borrower step: its fields, and what it needs to know about the loan being
 * added/edited. Its actions follow EditStdLoanModal's onBClientOrEntitySelection and onBrokerSelect, and do nothing
 * on a sub note.
 */
export interface BorrowerSlice {
    /** The Borrower section's fields. */
    borrower: BorrowerForm;
    /** Stores what BorrowerSelector hands back (it clears whichever of the entity and agreement no longer applies). */
    setBorrowerSelection: (selection: BorrowerSelection) => void;
    /** Picks (or clears) the broker; there's never one alongside an agreement, which brings its own. */
    selectBroker: (brokerId: number | undefined) => void;

    /** Set by whoever opens the modal. */
    borrowerLoanContext: BorrowerLoanContext;
    setBorrowerLoanContext: (context: Partial<BorrowerLoanContext>) => void;
}

/** The Borrower slice's starting values; reset() puts these back. */
export const initialBorrowerState = {
    borrower: emptyBorrowerForm,
    borrowerLoanContext: defaultBorrowerLoanContext,
};

export const createBorrowerSlice: StateCreator<StandardLoanState, [], [], BorrowerSlice> = (set) => {
    // Every change is ignored on a sub note, whose borrower and broker come from its master note
    const updateBorrower = (getChanges: (borrower: BorrowerForm) => Partial<BorrowerForm>) =>
        set((state) =>
            state.setupLoanContext.isSubNote ? {} : { borrower: { ...state.borrower, ...getChanges(state.borrower) } }
        );

    return {
        ...initialBorrowerState,

        setBorrowerSelection: (selection) => updateBorrower(() => selection),

        selectBroker: (brokerId) =>
            updateBorrower((borrower) => ({ brokerId: borrower.brokerAgreementId ? undefined : brokerId })),

        setBorrowerLoanContext: (context) =>
            set((state) => ({ borrowerLoanContext: { ...state.borrowerLoanContext, ...context } })),
    };
};
